import { Component, Injector, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { NotificationType } from 'src/app/common/enumerations/enumerations';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import { NotificationTemplate } from 'src/app/components/catalogs/notifications/services/notification-template';
import { NotificationMessage } from 'src/app/components/catalogs/notifications/services/notification.models';
import { NotificationService } from 'src/app/components/catalogs/notifications/services/notification.service';
import { Checklist, ChecklistExecStatus, ChecklistTemplate, SaveChecklistStatusResource } from 'src/app/components/checklists/checklists';
import { ChecklistsService } from 'src/app/components/checklists/checklists.service';
import { ChecklistsUpdate } from 'src/app/components/checklists/store/checklists/checklists.action';
import { ClBaseComponent } from '../cl-base/cl-base.component';
import { YesNoDialogComponent } from '../../yes-no-dialog/yes-no-dialog.component';

export interface MultipleStatusChangeOption {
  id?: number;
  label?: string;
  status?: number | null;
  roles?: number[];
}

@Component({
  selector: 'cl-multiple-status-change',
  templateUrl: './cl-multiple-status-change.component.html',
  styleUrls: ['./cl-multiple-status-change.component.scss']
})
export class ClMultipleStatusChangeComponent extends ClBaseComponent implements OnInit, OnChanges, OnDestroy {

  @Input() currentChecklistTemplate?: ChecklistTemplate;

  currentChecklist?: Checklist;
  statusOptions: MultipleStatusChangeOption[] = [];
  statuses?: ChecklistExecStatus[];
  selectedOption?: MultipleStatusChangeOption;
  roles?: Role[];
  roles$!: Observable<Role[]>;
  rolesSubs!: Subscription;
  notificationTemplates?: NotificationTemplate[];
  notificationTemplates$!: Observable<NotificationTemplate[]>;
  notificationTemplatesSubs!: Subscription;

  constructor(
    protected override injector: Injector,
    private service: ChecklistsService,
    private notificationService: NotificationService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadRoles();
    this.loadNotificationTemplates();
  }

  override ngOnDestroy(): void {
    this.rolesSubs?.unsubscribe();
    this.notificationTemplatesSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  /**
   * Parses configured radio options and resolves the checklist status catalog.
   * @param changes Angular input changes from master-control.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.currentChecklist = this.checklist as Checklist;
    if (this.currentChecklist?.checklistTemplate) {
      this.currentChecklistTemplate = this.currentChecklist.checklistTemplate;
    }
    this.statuses = this.currentChecklistTemplate?.documentType?.checklistExecStatuses;
    this.loadOptions();
  }

  /**
   * Loads roles used by option-specific notification settings.
   */
  private loadRoles(): void {
    this.roles$ = this.store.select(state => state.Roles.data);
    this.rolesSubs = this.roles$.subscribe(data => {
      this.roles = data;
    });
  }

  /**
   * Loads notification templates needed to mirror cl-status-change email behavior.
   */
  private loadNotificationTemplates(): void {
    this.notificationTemplates$ = this.store.select(state => state.NotificationTemplates.data);
    this.notificationTemplatesSubs = this.notificationTemplates$.subscribe(data => {
      this.notificationTemplates = data;
    });
  }

  /**
   * Loads status-change options from task configuration.
   */
  private loadOptions(): void {
    const configuration = this.utils.JSONparse(this.configuration);
    this.statusOptions = (configuration?.options ?? []).map((option: MultipleStatusChangeOption, index: number) => ({
      ...option,
      id: option.id ?? index + 1
    }));
    // The selected radio is persisted as numericValue for condition evaluation.
    this.selectedOption = this.statusOptions.find(option => option.id == this.radioSelection);
  }

  /**
   * Confirms and applies the selected status transition.
   * @param option Selected radio option containing target status and notification roles.
   */
  setStatus(option: MultipleStatusChangeOption): void {
    if (!option?.status || this.checkDisabled())
      return;

    const targetStatus = this.statuses?.find(status => status.id == option.status);
    const resetCurrentSection = this.shouldResetCurrentSection(targetStatus);
    const confirmationMessage = resetCurrentSection
      ? 'This action will change the Status to <b>' + targetStatus?.description +
      '</b> and reset all tasks from the current Status to their default values. Are you sure you want to continue?'
      : 'Are you sure to change the Status to <b>' + targetStatus?.description + '</b> ?';
    const confirm = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: confirmationMessage,
        icon: resetCurrentSection ? 'warning' : 'question'
      }
    });

    confirm.afterClosed().toPromise().then(async data => {
      if (data && !(this.editor || this.builder)) {
        this.selectedOption = option;
        this.radioSelection = option.id;
        this.changed.emit(option.id);
        const saveChecklistStatus = {
          id: this.currentChecklist?.id,
          status: targetStatus?.id,
          checklistStatusID: targetStatus?.id,
          rolesToNotify: option.roles,
          wfTaskLocalID: this.signature?.wfTaskLocal?.id
        } as SaveChecklistStatusResource;
        await this.service.putStatus(saveChecklistStatus).toPromise().then(async checklist => {
          if (!checklist?.id) return;
          // Reload after transition so section-reset task values and signatures are reflected.
          const refreshedChecklist = await this.service.getById(checklist.id).toPromise();
          this.refreshChecklistStores(refreshedChecklist ?? checklist);
          await this.sendNotification(option, targetStatus);
        });
      }
    });
  }

  /**
   * Sends the status-change notification configured for the selected option.
   * @param option Selected radio option with notification roles.
   * @param targetStatus Status applied by the selected option.
   */
  private async sendNotification(option: MultipleStatusChangeOption, targetStatus?: ChecklistExecStatus): Promise<void> {
    if (!option.roles?.length || !targetStatus)
      return;

    const notificationTemplate = this.notificationTemplates?.find(template => template.type == NotificationType.ChecklistStatusChange);
    if (!notificationTemplate)
      return;

    let notificationMessage = await this.notificationService.getNotification(25, this.currentChecklist?.id ?? 0, [], targetStatus.id.toString()).toPromise().catch(error => {
      console.log(error);
      return undefined;
    });
    if (!notificationMessage || !notificationMessage.toAddresses.includes('<')) {
      const rolesToNotify = this.roles?.filter(role => option.roles?.includes(role.id)) ?? [];
      const toAddresses = this.users.filter(user =>
        this.utils.intersect(rolesToNotify.map(role => role.id), user.userRole?.map(userRole => userRole.roleID))
      ).map(user => `${user.name} <${user.emailAddress}>`);
      const messageBody = (notificationTemplate.messageBody ?? '')
        .replaceAll('{serialNo}', this.currentChecklist?.serialNo ?? '')
        .replaceAll('{statusName}', targetStatus.name ?? '');

      notificationMessage = {
        serialNo: this.currentChecklist?.serialNo,
        notificationId: 0,
        documentId: this.currentChecklist?.id,
        toAddresses: this.utils.JSONstringify({ computed: toAddresses }),
        messageSubject: notificationTemplate.messageSubject,
        messageBody,
        type: 25,
        code: targetStatus.id.toString(),
        messageAdditionalText: '',
        signature: notificationTemplate.signature,
      } as NotificationMessage;
      notificationMessage = await this.notificationService.createNotification(notificationMessage).toPromise().catch(error => {
        console.log(error);
        this.alert.defaultError();
        return undefined;
      });
    }

    if (notificationMessage) {
      await this.notificationService.sendNotification(notificationMessage).toPromise().then(() => {
        const roleCodes = this.roles?.filter(role => option.roles?.includes(role.id)).map(role => role.code).join(', ');
        this.alert.info('Email Notifications sent to <b>' + roleCodes + '</b>');
      }).catch(error => {
        console.log(error);
        this.alert.defaultError();
      });
    }
  }

  /**
   * Replaces checklist execution state after status changes and rebuilds flattened tasks.
   * @param checklist Fresh checklist resource returned by the API.
   */
  private refreshChecklistStores(checklist: Checklist): void {
    this.currentChecklist = checklist;
    this.store.dispatch(new ChecklistsUpdate(checklist.id, checklist));
    this.service.setAllTasks(checklist.wfTable?.wfTableLocal?.wfSectionLocals);
  }

  /**
   * Checks whether a lower-status transition should reset the current section.
   * @param targetStatus Status selected by the radio option.
   * @returns True when Template Type configuration requires a reset confirmation.
   */
  private shouldResetCurrentSection(targetStatus?: ChecklistExecStatus): boolean {
    const currentOrder = this.currentChecklist?.checklistStatus?.order;
    const targetOrder = targetStatus?.order;
    return this.currentChecklistTemplate?.templateType?.resetSectionOnStatusChange === true &&
      currentOrder != null &&
      targetOrder != null &&
      targetOrder < currentOrder;
  }

  /**
   * Mirrors cl-status-change disabled behavior for execution, builder, and editor modes.
   * @returns True when the radio choices should be disabled.
   */
  checkDisabled(): boolean {
    return !!this.disabled || !!this.builder;
  }
}
